import React, { useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Upload, Loader2, X } from 'lucide-react';
import { mediaUrl } from '@/lib/mediaUrl';

const KINDS = {
  image: { accept: 'image/*', hint: 'JPG, PNG, GIF, WebP or SVG · max 10MB' },
  video: { accept: 'video/mp4,video/webm,video/quicktime', hint: 'MP4, WebM or MOV · max 200MB. For long videos, YouTube or Vimeo is faster for learners' },
  audio: { accept: 'audio/mpeg,audio/mp4,audio/wav,audio/ogg,.mp3,.m4a,.wav,.ogg', hint: 'MP3, M4A, WAV or OGG · max 50MB' },
  captions: { accept: '.vtt,.srt', hint: '.vtt or .srt — .srt is converted to .vtt automatically' },
  file: { accept: '*', hint: 'max 10MB' },
};

// URL input + upload button (+ optional alt text) used across builder configs
export const MediaUploadField = ({ label, value, onChange, kind = 'image', alt, onAltChange, placeholder, testId = 'media', required = false }) => {
  const { api } = useAuth();
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const k = KINDS[kind] || KINDS.file;

  const upload = async (file) => {
    if (!file) return;
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (alt) fd.append('alt_text', alt);
      const res = await api.post('/media/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)),
      });
      onChange(res.data.url);
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setProgress(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testId}-field`}>
      <Label>{label}{required && <span className="text-red-500"> *</span>}</Label>
      <div className="flex gap-2">
        <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || 'Paste a URL or upload a file'} data-testid={`${testId}-url-input`} />
        <Button type="button" variant="outline" size="sm" className="shrink-0 h-10" disabled={progress !== null} onClick={() => inputRef.current?.click()} data-testid={`${testId}-upload-btn`}>
          {progress !== null ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          <span className="ml-1 hidden sm:inline">Upload</span>
        </Button>
        {value && (
          <Button type="button" variant="ghost" size="icon" className="shrink-0" onClick={() => onChange('')} data-testid={`${testId}-clear-btn`} aria-label="Remove file">
            <X className="h-4 w-4" />
          </Button>
        )}
        <input ref={inputRef} type="file" accept={k.accept} className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testId}-file-input`} />
      </div>
      {progress !== null && <Progress value={progress} className="h-1.5" />}
      <p className="text-[11px] text-slate-400">{k.hint}</p>
      {kind === 'image' && value && (
        <img src={mediaUrl(value)} alt={alt || ''} className="max-h-32 rounded-md border border-slate-200 object-contain bg-slate-50" />
      )}
      {kind === 'image' && onAltChange && (
        <div className="space-y-1">
          <Label className="text-xs">Alt text <span className="text-slate-400 font-normal">(describe the image for screen readers)</span></Label>
          <Input value={alt || ''} onChange={(e) => onAltChange(e.target.value)} placeholder="e.g. Carer helping an older man stand from a chair" data-testid={`${testId}-alt-input`} />
          {value && !alt && <p className="text-[11px] text-amber-600" data-testid={`${testId}-alt-warning`}>Add alt text so learners using screen readers know what the image shows.</p>}
        </div>
      )}
    </div>
  );
};
